import { useEffect, useState } from "react";
import axios from "axios";
import { ShoppingBag, ExternalLink, ShieldCheck, Truck, Package, Sparkles, ArrowRight, Search, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHOPIFY_URL = "https://turbo2transit.myshopify.com/";

// Curated category filters (map to Shopify tags; extras stay as request-only)
const CATEGORIES = [
  { key: "all", label: "All", tag: null },
  { key: "household", label: "Household", tag: "household" },
  { key: "electronics", label: "Electronics", tag: "electronics" },
  { key: "personal-care", label: "Personal Care", tag: "personal-care" },
  { key: "food", label: "Food & Grocery", tag: "food" },
  { key: "apparel", label: "Apparel", tag: "apparel" },
  { key: "baby-kids", label: "Baby & Kids", tag: "baby-kids" },
  { key: "auto-tools", label: "Auto & Tools", tag: "auto-tools" },
];

const Feature = ({ icon: Icon, title, desc }) => (
  <div className="flex gap-3 items-start rounded-2xl border border-[var(--tt-line)] bg-white p-4">
    <div className="grid place-items-center h-10 w-10 rounded-xl bg-[var(--tt-mint-soft)] text-[var(--tt-green)] shrink-0">
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <div className="font-semibold text-[var(--tt-navy)] text-sm">{title}</div>
      <div className="text-xs text-[var(--tt-muted)] mt-0.5 leading-relaxed">{desc}</div>
    </div>
  </div>
);

const money = (amount, currency) => {
  if (!amount) return "";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD" }).format(Number(amount));
  } catch {
    return `${currency || "$"} ${amount}`;
  }
};

const ProductCard = ({ p }) => (
  <a
    href={p.url}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`shop-product-${p.handle}`}
    className="group relative flex flex-col overflow-hidden rounded-2xl border border-[var(--tt-line)] bg-white hover:-translate-y-1 transition-all duration-300 hover:shadow-xl"
  >
    <div className="aspect-square bg-[var(--tt-cream)] overflow-hidden relative">
      {p.image ? (
        <img
          src={p.image}
          alt={p.image_alt || p.title}
          loading="lazy"
          className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      ) : (
        <div className="h-full w-full grid place-items-center text-[var(--tt-muted)]">
          <Package className="h-10 w-10" />
        </div>
      )}
      {!p.available && (
        <div className="absolute top-2 left-2 bg-red-100 text-red-700 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">
          Sold out
        </div>
      )}
    </div>
    <div className="p-3 flex flex-col gap-1 flex-1">
      <div className="text-[10px] uppercase tracking-widest text-[var(--tt-muted)]">{p.product_type || "Turbo Global"}</div>
      <div className="font-semibold text-sm text-[var(--tt-navy)] line-clamp-2 min-h-[2.5rem]">{p.title}</div>
      <div className="mt-auto flex items-center justify-between pt-2">
        <div className="font-black text-[var(--tt-green)]">{money(p.price, p.currency)}</div>
        <div className="text-[11px] font-semibold text-[var(--tt-navy)] inline-flex items-center gap-1 group-hover:text-[var(--tt-green)]">
          View <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </div>
  </a>
);

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [searchQ, setSearchQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const cat = CATEGORIES.find((c) => c.key === activeCat);
    const params = { limit: 24 };
    if (cat?.tag) params.tag = cat.tag;
    if (searchQ) params.q = searchQ;
    axios
      .get(`${API}/shop/products`, { params })
      .then((r) => {
        if (cancelled) return;
        setConfigured(Boolean(r.data.configured));
        setProducts(r.data.products || []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.response?.data?.detail || "Couldn't reach the store");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [activeCat, searchQ]);

  const onSearch = (e) => {
    e.preventDefault();
    setSearchQ(search.trim());
  };

  return (
    <div className="bg-[var(--tt-cream)] min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[var(--tt-line)]">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--tt-navy)] via-[var(--tt-navy-2)] to-[var(--tt-green)] opacity-95" />
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "24px 24px" }} />
        <div className="relative mx-auto max-w-7xl px-6 py-14 md:py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/20 px-3 py-1 text-xs font-semibold text-white uppercase tracking-widest mb-5">
              <Sparkles className="h-3.5 w-3.5" /> Now open
            </div>
            <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight">
              Shop at <span className="text-[var(--tt-accent-2)]">Turbo Global Store</span>
            </h1>
            <p className="text-white/85 text-base md:text-lg mt-5 max-w-2xl">
              Everyday essentials and specialty goods — sourced, stored, and shipped by the same
              team that moves freight for thousands of businesses. Fast dispatch, honest pricing,
              real support.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={SHOPIFY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="shop-open-store-btn"
                className="inline-flex items-center gap-2 rounded-full bg-white text-[var(--tt-navy)] font-bold px-6 py-3 text-sm shadow-lg hover:bg-[var(--tt-accent-2)] hover:text-[var(--tt-navy)] transition"
              >
                <ShoppingBag className="h-4 w-4" /> Browse the store
                <ExternalLink className="h-3.5 w-3.5 opacity-70" />
              </a>
              <a
                href="/contact"
                data-testid="shop-request-product-btn"
                className="inline-flex items-center gap-2 rounded-full border-2 border-white/60 text-white font-semibold px-6 py-3 text-sm hover:bg-white/10 transition"
              >
                Request a product
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="mx-auto max-w-7xl px-6 py-10">
        <div className="grid md:grid-cols-4 gap-3">
          <Feature icon={Truck} title="Fast dispatch" desc="Most orders leave our hub within 24 hours." />
          <Feature icon={ShieldCheck} title="Secure checkout" desc="Encrypted payments, buyer protection built in." />
          <Feature icon={Package} title="Real inventory" desc="Live stock — no surprise back-orders." />
          <Feature icon={Sparkles} title="Curated goods" desc="Household, tech, personal care and more." />
        </div>
      </section>

      {/* Filter + search bar */}
      <section className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between mb-6">
          <div className="flex flex-wrap gap-2" data-testid="shop-category-bar">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setActiveCat(c.key)}
                data-testid={`shop-cat-${c.key}`}
                className={`text-xs font-semibold px-3.5 py-2 rounded-full transition border ${
                  activeCat === c.key
                    ? "bg-[var(--tt-navy)] text-white border-[var(--tt-navy)]"
                    : "bg-white text-[var(--tt-navy)] border-[var(--tt-line)] hover:border-[var(--tt-green)] hover:text-[var(--tt-green)]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <form onSubmit={onSearch} className="flex items-center gap-2 bg-white border border-[var(--tt-line)] rounded-full pl-4 pr-1 py-1.5 max-w-md w-full lg:w-auto">
            <Search className="h-4 w-4 text-[var(--tt-muted)]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products…"
              data-testid="shop-search-input"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-[var(--tt-muted)]"
            />
            <button
              type="submit"
              data-testid="shop-search-submit"
              className="rounded-full bg-[var(--tt-green)] text-white text-xs font-semibold px-4 py-1.5 hover:bg-[var(--tt-navy)] transition"
            >
              Search
            </button>
          </form>
        </div>
      </section>

      {/* Product grid / states */}
      <section className="mx-auto max-w-7xl px-6 pb-12">
        {loading && (
          <div className="grid place-items-center py-20 text-[var(--tt-muted)]" data-testid="shop-loading">
            <Loader2 className="h-8 w-8 animate-spin text-[var(--tt-green)] mb-3" />
            <div className="text-sm">Loading products…</div>
          </div>
        )}

        {!loading && !configured && (
          <div className="rounded-3xl border border-dashed border-[var(--tt-line)] bg-white p-10 text-center" data-testid="shop-not-configured">
            <ShoppingBag className="h-12 w-12 mx-auto text-[var(--tt-green)] mb-4" />
            <h3 className="font-display font-black text-2xl text-[var(--tt-navy)]">Live catalog coming online</h3>
            <p className="text-sm text-[var(--tt-muted)] mt-2 max-w-lg mx-auto">
              We're syncing products in the background. In the meantime, jump straight into the
              store — full catalog, live inventory, secure checkout.
            </p>
            <a
              href={SHOPIFY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[var(--tt-green)] text-white font-bold px-6 py-3 text-sm shadow-lg hover:bg-[var(--tt-navy)] transition"
              data-testid="shop-open-store-fallback"
            >
              <ShoppingBag className="h-4 w-4" /> Open Turbo Global Store
              <ExternalLink className="h-3.5 w-3.5 opacity-80" />
            </a>
          </div>
        )}

        {!loading && configured && error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-800">
            {error}
          </div>
        )}

        {!loading && configured && !error && products.length === 0 && (
          <div className="rounded-3xl border border-dashed border-[var(--tt-line)] bg-white p-10 text-center" data-testid="shop-empty">
            <Package className="h-12 w-12 mx-auto text-[var(--tt-muted)] mb-3" />
            <div className="font-semibold text-[var(--tt-navy)]">No products match this filter yet</div>
            <p className="text-sm text-[var(--tt-muted)] mt-1">
              Try another category or <a href="/contact" className="text-[var(--tt-green)] font-semibold hover:underline">request a product</a>.
            </p>
          </div>
        )}

        {!loading && configured && !error && products.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="shop-product-grid">
            {products.map((p) => (
              <ProductCard key={p.id} p={p} />
            ))}
          </div>
        )}
      </section>

      {/* Bottom CTA */}
      <section className="mx-auto max-w-7xl px-6 pb-16">
        <div className="rounded-3xl border border-[var(--tt-line)] bg-gradient-to-br from-white to-[var(--tt-mint-soft)] p-8 md:p-10 flex flex-col md:flex-row items-center gap-6 justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[var(--tt-green)] font-semibold">Can't find what you need?</div>
            <h3 className="font-display font-black text-[var(--tt-navy)] text-2xl md:text-3xl mt-1 tracking-tight">
              Tell us what to stock next.
            </h3>
            <p className="text-[var(--tt-muted)] text-sm mt-2 max-w-lg">
              We'll source it, store it, and ship it — fast. Send your request and our team
              will get back within 24 hours.
            </p>
          </div>
          <a
            href="/contact"
            data-testid="shop-request-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--tt-navy)] text-white font-bold px-6 py-3 text-sm shadow-lg hover:bg-[var(--tt-green)] transition whitespace-nowrap"
          >
            Request a product <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Shop;
